import React, { useCallback, useEffect, useState } from "react";

import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";

import { useDispatch } from "react-redux";
import Table from "components/Table/Table";
import InformationBox from "components/common/Message/InformationBox/InformationBox";
import ViewFileModal from "components/common/ViewFileModal/ViewFileModal";
import { ATTACHMENT_LIST_COLS } from "./AttachmentListData";
import {
  IMAGE_UPLOAD_DATAFIELDS,
  DAMPER_IMAGE_UPLOADS,
  NFPA_CLASSIFICATION_DATA_FIELD_ID,
} from "components/common/Forms/Utility/ImageUploadFields";
import {
  downloadEquipmentAttachment,
  getFieldAttachments,
  onViewEquipmentAttachment,
} from "redux/pages/Inspection/InspectionTask/InspectionDetails/Equipment/EquipmentSlice";

interface Props {
  showModal: boolean;
  setShowModal: (state: boolean) => void;
  uniqueInspectionTaskId: any;
  selectedEquipments: any;
  selectedTask?: any;
}

interface CategoryOption {
  label: string;
  value: string;
  dataFieldId: number;
  fileCategoryId?: number;
}

const CATEGORY_OPTIONS: CategoryOption[] = [
  ...Object.keys(IMAGE_UPLOAD_DATAFIELDS).map((dataFieldId) => ({
    label: IMAGE_UPLOAD_DATAFIELDS[Number(dataFieldId)].fieldLabel,
    value: `field-${dataFieldId}`,
    dataFieldId: Number(dataFieldId),
  })),
  ...DAMPER_IMAGE_UPLOADS.map((config) => ({
    label: config.fieldLabel,
    value: `damper-${config.fileCategoryId}`,
    dataFieldId: NFPA_CLASSIFICATION_DATA_FIELD_ID,
    fileCategoryId: config.fileCategoryId,
  })),
].sort((a, b) => a.label.localeCompare(b.label));

export const AttachmentList: React.FC<Props> = ({
  showModal = false,
  setShowModal = () => { },
  uniqueInspectionTaskId = "",
  selectedEquipments = [],
}) => {

  const dispatch = useDispatch<any>();

  const [selectedCategory, setSelectedCategory] = useState<string>(CATEGORY_OPTIONS[0]?.value);
  const [attachments, setAttachments] = useState<any[]>([]);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [displayInfoModal, setDisplayInfoModal] = useState(false);
  const [message, setMessage] = useState("");
  const [showViewModal, setShowViewModal] = useState(false);
  const [streamData, setStreamData] = useState<any>("");
  const [sortField, setSortField] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<string>("");

  const equipmentId = selectedEquipments[0]?.EquipmentId;

  const loadAttachments = useCallback((categoryValue: string, sortColumn: string, sortDirection: string) => {
    if (!uniqueInspectionTaskId || !equipmentId) return;
    const option = CATEGORY_OPTIONS.find((item) => item.value === categoryValue);
    if (!option) return;

    dispatch(getFieldAttachments(uniqueInspectionTaskId, equipmentId, option.dataFieldId, (attachmentList: any[]) => {
      setAttachments(attachmentList ?? []);
      setSelectedRows([]);
    }, option.fileCategoryId, sortColumn, sortDirection));
  }, [dispatch, uniqueInspectionTaskId, equipmentId]);

  useEffect(() => {
    loadAttachments(selectedCategory, sortField, sortOrder);
  }, [selectedCategory, sortField, sortOrder, loadAttachments]);

  const onSort = (event: any) => {
    setSortField(event?.sortField ?? "");
    setSortOrder(event?.sortOrder === 1 ? "ASC" : "DESC");
  };

  const handleRowSelect = useCallback((rows: any) => {
    setSelectedRows([...rows]);
  }, []);

  const requireSingleRow = () => {
    if (selectedRows.length < 1) {
      setMessage("Select an Attachment");
      setDisplayInfoModal(true);
      return false;
    }
    if (selectedRows.length > 1) {
      setMessage("This operation can be performed only one row at a time");
      setDisplayInfoModal(true);
      return false;
    }
    return true;
  };

  const onDownload = () => {
    if (!requireSingleRow()) return;
    dispatch(downloadEquipmentAttachment(
      uniqueInspectionTaskId,
      equipmentId,
      selectedRows[0]?.attachmentId,
      selectedRows[0]?.fileName,
      setMessage,
      setDisplayInfoModal,
    ));
  };

  const openViewModal = () => {
    if (!requireSingleRow()) return;
    dispatch(onViewEquipmentAttachment(
      uniqueInspectionTaskId,
      equipmentId,
      selectedRows[0]?.attachmentId,
      selectedRows[0]?.fileName,
      setMessage,
      setDisplayInfoModal,
      setStreamData,
      setShowViewModal,
    ));
  };

  return (
    <>
      <Dialog
        header="Attachments"
        visible={showModal}
        style={{ width: "50vw" }}
        onHide={() => setShowModal(false)}
        className="display-settings personal selectsite"
      >
        {/* Address on the left, category filter at the right end. An inline label is used rather
            than a p-float-label: the floating variant sits above the control's border, which
            breaks the shared baseline with the address and clips against the dialog edge.
            Dropdown sizing and focus colour come from .category-filter in DisplaySetting.scss. */}
        <div className="d-flex align-items-center justify-content-between pb-2" style={{ gap: "1.5rem" }}>
          <div className="header mb-0 d-flex align-items-center">
            <span className="title">Address:</span>
            <span className="value">{selectedEquipments[0]?.Address}</span>
          </div>

          <div className="category-filter d-flex align-items-center">
            <label htmlFor="attachment-category" className="filter-label">Category:</label>
            <Dropdown
              inputId="attachment-category"
              value={selectedCategory}
              options={CATEGORY_OPTIONS}
              optionLabel="label"
              optionValue="value"
              onChange={(e) => setSelectedCategory(e.value)}
            />
          </div>
        </div>

        <Table
          rows={attachments}
          cols={ATTACHMENT_LIST_COLS.LIST_COLS}
          dataKeyId="attachmentId"
          title={``}
          showDownloadIcon={true}
          showSettingIcon={false}
          showHamIcon={false}
          showAddIcon={false}
          showEditIcon={false}
          showDeleteIcon={false}
          searchboxfield={false}
          showroweditor={false}
          getSelectedRowData={handleRowSelect}
          onSortData={onSort}
          onDownloadClick={onDownload}
          hideDownloadCaret={true}
          showViewIcon={true}
          showDislaySettingsIcon={false}
          onViewClick={openViewModal}
          defaultSelectedRows={attachments.length > 0 ? [attachments[0]] : []}
          singleSelect={true}
        />

        <InformationBox
          showInfoModal={displayInfoModal}
          setShowInfoModal={setDisplayInfoModal}
          message={message}
        />
      </Dialog>
      {showViewModal && streamData &&
        <ViewFileModal
          showModal={showViewModal}
          setStreamData={setStreamData}
          setShowModal={setShowViewModal}
          streamData={streamData}
          fileName={selectedRows[0]?.fileName}
        />
      }
    </>
  );
};

export default AttachmentList;
